import { useEffect, useMemo, useState } from 'react'
import { executionsApi } from '../../services/api'
import { runEndRecordOf, type RunEndRecord } from '../../utils/run-end'
import { ENDED_STATUSES, STORED_RUN_RETRIES, STORED_RUN_RETRY_MS } from './useLaunchedRunEnding'

/** A run record the chat already read (GET /v1/executions/{id}), for the run it names. */
export interface RecordedRun {
  executionId: string
  execution: { status: string }
}

/**
 * An ended run's end record (P-095 slice 8, agion/Agion#2939), read from
 * GET /v1/executions/{id}: the chat words why the run ended and what it
 * lacked (runEndReading) and what it shortened (runCutNote, slice 11B-4).
 * A run opened from history is read from the record the chat already read;
 * a run that ended on screen is read once it ends. The live stream can report
 * the end before the platform has stored it, so a record whose status has
 * not ended yet is read again a bounded number of times. Null while the run
 * is open, while the read is pending, when it fails, and when the server
 * sends no result (MISSION_RUN_RESULT_ENABLED off), so the chat keeps
 * today's end lines.
 */
export function useRunEndRecord(executionId: string | null, ended: boolean, recorded: RecordedRun | null): RunEndRecord | null {
  const known = useMemo(
    () =>
      recorded && recorded.executionId === executionId && ENDED_STATUSES.includes(recorded.execution.status)
        ? { value: runEndRecordOf(recorded.execution) }
        : null,
    [recorded, executionId],
  )
  const [reading, setReading] = useState<{ executionId: string; value: RunEndRecord | null } | null>(null)
  useEffect(() => {
    if (!executionId || !ended || known) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const attempt = async (left: number) => {
      timer = null
      try {
        const execution = await executionsApi.getExecution(executionId)
        if (cancelled) return
        if (!ENDED_STATUSES.includes(execution.status) && left > 0) {
          timer = setTimeout(() => { void attempt(left - 1) }, STORED_RUN_RETRY_MS)
          return
        }
        setReading({ executionId, value: runEndRecordOf(execution) })
      } catch (err) {
        console.warn('[StreamingCustomerChat] Failed to read the run end record:', err)
      }
    }
    void attempt(STORED_RUN_RETRIES)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [executionId, ended, known])
  if (!ended) return null
  if (known) return known.value
  // An answer for another run is never shown for this one.
  return reading && reading.executionId === executionId ? reading.value : null
}
